import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";

import { SceneManager } from "../core/scene_manager.js";
import { PhysicsWorld } from "../physics/physics_world.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import { ResourceID } from "../resource_loader/resource_id.js";
import { TextureTest } from "../debug/texture_test.js";
import { SimulationContext } from "../simulation/simulation_context.js";
import { PhysicsFriction, PhysicsRestitution } from "../physics/physics_masses.js";
import { CurveTrajectoryController, TrajectoryConfig } from "../utils/curve_trajectory_controller.js";
import { Vfx3DManager } from "../vfx_3d/vfx_3d_manager.js";
import { IController } from "../vfx_3d/core/types/controller_types.js";
import { BallTuner } from "../debug/ball_tuner.js";
import { CannonConfigService } from "../cannon_config/cannon_config_service.js";

export class CannonBall
{
  private static readonly ROCKET_LENGTH = 0.8;
  private static readonly ROCKET_LAUNCH_HOLD_SECONDS = 0.08;
  private static readonly ROCKET_TURN_SECONDS = 0.22;
  private static readonly ROCKET_UP = new THREE.Vector3( 0, 1, 0 );

  public readonly mesh: THREE.Object3D;
  public readonly body: RAPIER.RigidBody;
  public readonly collider: RAPIER.Collider;
  public readonly isFirework: boolean;
  public readonly canHitMultipleTargets: boolean;

  public alive = true;
  public has_exploded = false;
  public has_hit_table = false;
  public has_triggered_big_ball_impact = false;

  private lifetime = 0;
  private fading = false;
  private readonly hit_collider_handles = new Set<number>();
  private readonly pre_physics_velocity = new THREE.Vector3();
  private jar_penetrations = 0;
  private rocket_launch_elapsed = 0;
  private rocket_launch_velocity: THREE.Vector3 | null = null;
  private readonly rocket_start_rotation = new THREE.Quaternion();
  private readonly rocket_current_rotation = new THREE.Quaternion();
  private readonly current_flight_dir = new THREE.Vector3( 0, 1, 0 );

  private trajectory_controller: CurveTrajectoryController | null = null;
  private trail_controller: IController | null = null;
  private last_curve_pos: THREE.Vector3 | null = null;
  private ground_shadow: THREE.Mesh | null = null;

  private radius = 0;

  public getRadius(): number
  {
    return this.radius;
  }

  private alpha = 1;
  private readonly max_lifetime = SimulationContext.enabled ? 1.2 : 3;
  private readonly fade_speed = SimulationContext.enabled ? 10 : 2;

  constructor(
    position: THREE.Vector3,
    direction: THREE.Vector3,
    size_multiplier = 1,
    mass_multiplier = 1,
    is_firework = false,
    is_passive_rocket = false
  )
  {
    if ( !Number.isFinite( size_multiplier ) || size_multiplier <= 0 )
      throw new Error( "Cannon ball size multiplier must be a finite positive number" );

    if ( !Number.isFinite( mass_multiplier ) || mass_multiplier <= 0 )
      throw new Error( "Cannon ball mass multiplier must be a finite positive number" );

    this.isFirework = is_firework;
    this.canHitMultipleTargets = size_multiplier > 1 && !is_firework;

    this.mesh = this.createMesh( size_multiplier, is_firework, is_passive_rocket );
    this.mesh.renderOrder = 10;
    this.mesh.position.copy( position );
    const box = new THREE.Box3().setFromObject( this.mesh );
    const size = box.getSize( new THREE.Vector3() );

    this.radius = is_firework
      ? Math.max( size.x, size.z ) * 0.5
      : Math.max( size.x, size.y, size.z ) * 0.5;

    if ( this.canHitMultipleTargets )
      this.ground_shadow = this.createGroundShadow();
    
    this.body = this.createRigidBody( position );
    this.body.enableCcd( true );

    // this.body.setLinearDamping( 0.3 );
    this.body.setAngularDamping( 0.5 );

    this.collider = this.createCollider( mass_multiplier );

    if ( is_firework )
    {
      this.rocket_launch_velocity = direction.clone();
      this.body.setGravityScale( 0, true );
      this.collider.setEnabled( false );
      this.trail_controller = Vfx3DManager.addFireworkTrail( position );
    }
    else
      this.setVelocity( direction );
  }

  public static cast_shadow = true;

  // Building a material per shot forces a shader rebuild mid-flight. Regular
  // balls, boosted balls, and rockets each reuse their own material and only
  // clone it when an individual projectile starts fading out.
  private static regular_material: THREE.MeshLambertMaterial | null = null;
  private static big_ball_material: THREE.MeshStandardMaterial | null = null;
  private static rocket_material: THREE.MeshStandardMaterial | null = null;
  private static passive_rocket_material: THREE.MeshStandardMaterial | null = null;

  private static getRocketMaterial( passive: boolean ): THREE.MeshStandardMaterial
  {
    const cached = passive ? this.passive_rocket_material : this.rocket_material;
    if ( cached )
      return cached;

    const material = new THREE.MeshStandardMaterial( {
      map: ResourceManager.getTexture( passive ? ResourceID.RocketPassiveTexture : ResourceID.RocketTexture ),
      roughness: 0.35,
      metalness: 0
    } );
    if ( passive )
      this.passive_rocket_material = material;
    else
      this.rocket_material = material;
    return material;
  }

  private static getSharedMaterial(
    big_ball: boolean
  ): THREE.MeshLambertMaterial | THREE.MeshStandardMaterial
  {
    const cached = big_ball ? this.big_ball_material : this.regular_material;
    if ( cached )
      return cached;

    if ( !big_ball )
    {
      this.regular_material = new THREE.MeshLambertMaterial( {
        map: ResourceManager.getTexture( ResourceID.CannonBaseMap ),
        normalMap: ResourceManager.getNormalMap( ResourceID.CannonNormalMap ),
        normalScale: new THREE.Vector2( 0.3, 0.3 )
      } );
      return this.regular_material;
    }

    const map = ResourceManager.getTexture( ResourceID.CannonModelBaseMap );
    this.big_ball_material = new THREE.MeshStandardMaterial( {
      map,
      normalMap: ResourceManager.getNormalMap( ResourceID.CannonModelNormalMap ),
      normalScale: new THREE.Vector2( 0.3, 0.3 ),
      roughness: 0.24,
      metalness: 0,
      // Keep the shaded half readable without flattening the normal-map
      // relief or changing the authored colour on the lit side.
      emissive: new THREE.Color( 0xffffff ),
      emissiveMap: map,
      emissiveIntensity: 0.24,
    } );
    return this.big_ball_material;
  }

  public static createWarmupProbe(): THREE.Mesh
  {
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry( 0.01, 0.01, 0.01 ),
      this.getSharedMaterial( false ).clone()
    );

    mesh.castShadow = this.cast_shadow;
    mesh.receiveShadow = true;
    mesh.renderOrder = 10;

    return mesh;
  }

  private static plain_geometry: THREE.SphereGeometry | null = null;
  private static plain_material: THREE.MeshBasicMaterial | null = null;
  private static ground_shadow_geometry: THREE.PlaneGeometry | null = null;
  private static ground_shadow_texture: THREE.CanvasTexture | null = null;

  private static getGroundShadowTexture(): THREE.CanvasTexture
  {
    if ( this.ground_shadow_texture )
      return this.ground_shadow_texture;

    const canvas = document.createElement( "canvas" );
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext( "2d" );
    if ( !ctx )
      throw new Error( "Unable to create big-ball ground shadow texture" );

    const gradient = ctx.createRadialGradient( 32, 32, 2, 32, 32, 31 );
    gradient.addColorStop( 0, "rgba(5, 14, 8, 0.96)" );
    gradient.addColorStop( 0.52, "rgba(5, 14, 8, 0.62)" );
    gradient.addColorStop( 0.82, "rgba(5, 14, 8, 0.24)" );
    gradient.addColorStop( 1, "rgba(5, 14, 8, 0)" );
    ctx.fillStyle = gradient;
    ctx.fillRect( 0, 0, 64, 64 );

    this.ground_shadow_texture = new THREE.CanvasTexture( canvas );
    this.ground_shadow_texture.colorSpace = THREE.SRGBColorSpace;
    this.ground_shadow_texture.needsUpdate = true;
    return this.ground_shadow_texture;
  }

  private createGroundShadow(): THREE.Mesh
  {
    if ( !CannonBall.ground_shadow_geometry )
      CannonBall.ground_shadow_geometry = new THREE.PlaneGeometry( 1, 1 );

    const material = new THREE.MeshBasicMaterial( {
      map: CannonBall.getGroundShadowTexture(),
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false,
    } );
    const shadow = new THREE.Mesh( CannonBall.ground_shadow_geometry, material );
    shadow.rotation.x = -Math.PI * 0.5;
    shadow.position.set( this.mesh.position.x, 0.006, this.mesh.position.z );
    shadow.renderOrder = 9;
    shadow.castShadow = false;
    shadow.receiveShadow = false;
    SceneManager.world.add( shadow );
    return shadow;
  }

  private updateGroundShadow(): void
  {
    const shadow = this.ground_shadow;
    if ( !shadow )
      return;

    const height = Math.max( 0, this.mesh.position.y - this.radius );
    const height_scale = 1 + Math.min( 0.42, height * 0.055 );
    const diameter = this.radius * 2.25 * height_scale;
    shadow.position.set( this.mesh.position.x, 0.006, this.mesh.position.z );
    shadow.scale.set( diameter, diameter, 1 );

    const material = shadow.material as THREE.MeshBasicMaterial;
    const height_opacity = THREE.MathUtils.clamp( 1 - height / 10, 0.3, 1 );
    material.opacity = 0.5 * height_opacity * this.alpha;
  }

  private removeGroundShadow(): void
  {
    if ( !this.ground_shadow )
      return;

    SceneManager.world.remove( this.ground_shadow );
    ( this.ground_shadow.material as THREE.Material ).dispose();
    this.ground_shadow = null;
  }

  private createPlainMesh( size_multiplier: number ): THREE.Object3D
  {
    if ( !CannonBall.plain_geometry )
      CannonBall.plain_geometry = new THREE.SphereGeometry( 0.2235, 8, 6 );

    if ( !CannonBall.plain_material )
      CannonBall.plain_material = new THREE.MeshBasicMaterial( { color: 0xff3d00 } );

    const ball = new THREE.Mesh( CannonBall.plain_geometry, CannonBall.plain_material );
    ball.scale.setScalar( size_multiplier );
    ball.castShadow = false;
    ball.receiveShadow = false;

    const mesh = new THREE.Group();
    mesh.add( ball );

    SceneManager.world.add( mesh );

    return mesh;
  }

  public static force_plain = false;

  private createMesh( size_multiplier: number, is_firework: boolean, is_passive_rocket: boolean ): THREE.Object3D
  {
    if ( !is_firework && ( TextureTest.isActive() || CannonBall.force_plain ) )
      return this.createPlainMesh( size_multiplier );

    const model = ResourceManager.getModel(
      is_firework ? ResourceID.RocketModel : ResourceID.CannonBallModel
    );

    if ( is_firework )
    {
      const authored_size = new THREE.Box3()
        .setFromObject( model )
        .getSize( new THREE.Vector3() );
      model.scale.setScalar( CannonBall.ROCKET_LENGTH / authored_size.y );
    }
    else
    {
      // Match the original cannon_ball.glb diameter (about 0.447 world units).
      model.scale.setScalar( 0.1377 * size_multiplier );
    }

    const material = is_firework
      ? CannonBall.getRocketMaterial( is_passive_rocket )
      : CannonBall.getSharedMaterial( size_multiplier > 1 );

    model.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) )
        return;

      // The boosted ball uses a dedicated floor shadow. Excluding it from the
      // directional shadow map prevents shadows on the table and blocks.
      child.castShadow = CannonBall.cast_shadow && size_multiplier <= 1;
      child.material = material;
    } );

    // The FBX origin is above the geometric centre. Centre the artwork inside
    // a wrapper so its visual sphere and the physics collider share an origin.
    const bounds = new THREE.Box3().setFromObject( model );
    const centre = bounds.getCenter( new THREE.Vector3() );
    model.position.sub( centre );

    const mesh = new THREE.Group();
    mesh.add( model );

    SceneManager.world.add( mesh );

    return mesh;
  }

  public setRocketBezierTrajectory( config: TrajectoryConfig ): void
  {
    this.trajectory_controller = new CurveTrajectoryController( config );
    
    const res = this.trajectory_controller.update( 0 );
    
    this.rocket_start_rotation.setFromUnitVectors( CannonBall.ROCKET_UP, res.tangent );
    this.rocket_current_rotation.copy( this.rocket_start_rotation );

    this.body.setTranslation( { x: res.position.x, y: res.position.y, z: res.position.z }, true );
    this.body.setRotation( this.rocket_start_rotation, true );
    
    this.body.setGravityScale( 0, true );
    this.body.setLinvel( { x: 0, y: 0, z: 0 }, true );
    this.body.setAngvel( { x: 0, y: 0, z: 0 }, true );
    
    this.collider.setEnabled( true );
    this.mesh.quaternion.copy( this.rocket_start_rotation );
  }

  private updateRocketLaunch( dt: number ): void
  {
    if ( !this.trajectory_controller || this.trajectory_controller.isFinished() )
      return;

    const current_pos = this.body.translation();
    const current_vec = new THREE.Vector3( current_pos.x, current_pos.y, current_pos.z );
    const res = this.trajectory_controller.update( dt );

    // Teleport to the exact curve position to eliminate any physics bouncing or overshooting
    this.body.setTranslation( { x: res.position.x, y: res.position.y, z: res.position.z }, true );

    // Apply linear velocity only to ensure Continuous Collision Detection (CCD) sweeps the path correctly.
    // Use the exact curve displacement instead of current_vec to prevent chaotic velocity spikes if the body bounced.
    if ( !this.last_curve_pos )
      this.last_curve_pos = current_vec;
    
    const velocity = new THREE.Vector3().subVectors( res.position, this.last_curve_pos );
    velocity.divideScalar( Math.max( dt, 0.001 ) );
    
    this.body.setLinvel( { x: velocity.x, y: velocity.y, z: velocity.z }, true );
    this.last_curve_pos.copy( res.position );

    this.rocket_start_rotation.setFromUnitVectors( CannonBall.ROCKET_UP, res.tangent );
    this.body.setRotation( this.rocket_start_rotation, true );
    this.current_flight_dir.copy( res.tangent ).normalize();

    if ( this.trajectory_controller.isFinished() )
    {
      this.body.setGravityScale( 1, true );
      this.setVelocity( res.tangent.multiplyScalar( 25 ) );
    }
  }

  public getFlightDirection(): THREE.Vector3
  {
    const linvel = this.body.linvel();
    const dir = new THREE.Vector3( linvel.x, linvel.y, linvel.z );
    if ( dir.lengthSq() > 0.0001 )
      return dir.normalize();

    if ( this.current_flight_dir.lengthSq() > 0.0001 )
      return this.current_flight_dir.clone().normalize();

    return new THREE.Vector3( 0, 1, 0 ).applyQuaternion( this.mesh.quaternion ).normalize();
  }

  private createRigidBody(
    position: THREE.Vector3
  ): RAPIER.RigidBody
  {
    return PhysicsWorld.getWorld().createRigidBody(
      RAPIER.RigidBodyDesc.dynamic().setTranslation(
        position.x,
        position.y,
        position.z
      )
    );
  }

  private createCollider( mass_multiplier: number ): RAPIER.Collider
  {
    const collider = PhysicsWorld.getWorld().createCollider(
      RAPIER.ColliderDesc.ball( this.radius )
        .setRestitution( PhysicsRestitution.cannon_ball )
        .setFriction( PhysicsFriction.cannon_ball ),
      this.body
    );

    collider.setMass( BallTuner.applyMass( CannonConfigService.get().ball_mass * mass_multiplier ) );

    return collider;
  }

  public contactPointWith( other: RAPIER.Collider ): THREE.Vector3 | null
  {
    let contact_point: THREE.Vector3 | null = null;

    PhysicsWorld.getWorld().contactPair( this.collider, other, manifold =>
    {
      if ( contact_point )
        return;

      for ( let i = 0; i < manifold.numSolverContacts(); i++ )
      {
        // Rapier may keep nearby predictive contacts in a manifold. Only a
        // touching/penetrating solver contact should trigger impact feedback.
        if ( manifold.solverContactDist( i ) > 0.001 )
          continue;

        const point = manifold.solverContactPoint( i );
        contact_point = new THREE.Vector3( point.x, point.y, point.z );
        break;
      }
    } );

    return contact_point;
  }

  /** Returns true only for the first processed hit against this collider. */
  public registerTargetHit( other: RAPIER.Collider ): boolean
  {
    if ( this.hit_collider_handles.has( other.handle ) )
      return false;

    this.hit_collider_handles.add( other.handle );
    return true;
  }

  /** Capture the unobstructed flight vector before Rapier resolves contacts. */
  public capturePrePhysicsVelocity(): void
  {
    if ( !this.alive || this.isFirework ) return;
    const velocity = this.body.linvel();
    this.pre_physics_velocity.set( velocity.x, velocity.y, velocity.z );
  }

  public tryUseJarPenetration(): boolean
  {
    // Three pass-through hits plus the final stopping hit = four broken jars.
    if ( this.isFirework || this.canHitMultipleTargets || this.jar_penetrations >= 3 )
      return false;
    this.jar_penetrations++;
    return true;
  }

  /**
   * Jars are destructible pass-through targets. Rapier still gives them a real
   * impact, but the projectile keeps its incoming direction and most of its
   * speed so a well-aligned shot can break several jars in a row.
   */
  public preserveVelocityAfterJarHit( retention = 0.9 ): void
  {
    const incoming_speed = this.pre_physics_velocity.length();
    if ( incoming_speed < 0.1 ) return;

    const speed = incoming_speed * THREE.MathUtils.clamp( retention, 0, 1 );
    const velocity = this.pre_physics_velocity.setLength( speed );
    this.body.setLinvel( { x: velocity.x, y: velocity.y, z: velocity.z }, true );
  }

  private setVelocity(
    direction: THREE.Vector3
  ): void
  {
    this.body.setLinvel(
      {
        x: direction.x,
        y: direction.y,
        z: direction.z
      },
      true
    );

    // Spin the rigid body itself so contacts and friction can affect the
    // rotation. For a shot moving along the ground, velocity × up gives the
    // natural rolling axis.
    const rolling_axis = direction.clone()
      .cross( new THREE.Vector3( 0, 1, 0 ) );
    const speed = direction.length();

    if ( !this.isFirework && rolling_axis.lengthSq() > 0.0001 && speed > 0.1 )
    {
      rolling_axis.normalize().multiplyScalar( speed * 0.21 );
      this.body.setAngvel(
        {
          x: rolling_axis.x,
          y: rolling_axis.y,
          z: rolling_axis.z
        },
        true
      );
    }
  }

  private updateFade(
    dt: number
  ): void
  {
    this.alpha -= this.fade_speed * dt;

    this.mesh.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) )
        return;

      const source = Array.isArray( child.material ) ? child.material : [ child.material ];
      if ( source.some( m => ( m as THREE.Material ).type === "MeshBasicMaterial" ) )
        return;

      if ( !child.userData.materialCloned )
      {
        child.material = Array.isArray( child.material )
          ? child.material.map( m => m.clone() )
          : child.material.clone();
        child.userData.materialCloned = true;
        child.castShadow = false;
      }

      const materials = Array.isArray( child.material )
        ? child.material
        : [child.material];

      for ( const material of materials )
      {
        if (
          !( material instanceof THREE.MeshLambertMaterial ) &&
          !( material instanceof THREE.MeshStandardMaterial )
        )
          continue;

        if ( !material.transparent )
        {
          material.transparent = true;
          material.depthWrite = false;
          material.needsUpdate = true;
        }
        material.opacity = this.alpha;
      }
    } );

    if ( this.alpha <= 0 )
    {
      this.destroy();
    }
  }

  public update( dt: number ): void
  {
    if ( !this.alive )
      return;

    this.updateRocketLaunch( dt );

    const firework_still_on_curve = this.isFirework && this.trajectory_controller !== null && !this.trajectory_controller.isFinished();

    if ( !firework_still_on_curve )
    {
      this.lifetime += dt;
      if ( this.lifetime >= this.max_lifetime && !this.fading )
        this.fading = true;
    }

    const position = this.body.translation();

    // Trigger fade out early if it falls to the ground
    if ( position.y < 0.25 && !this.fading && !firework_still_on_curve )
      this.fading = true;
    const rotation = this.body.rotation();

    this.mesh.position.set(
      position.x,
      position.y,
      position.z
    );

    this.mesh.quaternion.set(
      rotation.x,
      rotation.y,
      rotation.z,
      rotation.w
    );

    this.updateGroundShadow();

    if ( this.trail_controller && this.alive )
    {
      this.trail_controller.setPosition( this.mesh.position );
    }

    if ( this.fading )
    {
      if ( this.isFirework )
      {
        this.destroy();
        return;
      }
      this.updateFade( dt );
    }

  }
  public stopTrail(): void
  {
    if ( this.trail_controller )
    {
      this.trail_controller.stop();
      this.trail_controller = null;
    }
  }

  public forceFadeOut(): void
  {
    if ( !this.alive || this.fading )
      return;

    this.stopTrail();
    if ( this.isFirework )
    {
      this.destroy();
    }
    else
    {
      this.fading = true;
    }
  }

  public vanish(): void
  {
    if ( !this.alive || this.isFirework )
      return;

    this.stopTrail();
    this.fading = true;
  }

  public detonate(): void
  {
    if ( !this.alive )
      return;

    this.stopTrail();
    this.collider.setEnabled( false );
    this.body.setLinvel( { x: 0, y: 0, z: 0 }, true );
    this.body.setAngvel( { x: 0, y: 0, z: 0 }, true );
    this.body.setGravityScale( 0, true );
    if ( this.isFirework )
    {
      this.destroy();
    }
    else
    {
      this.forceFadeOut();
    }
  }

  public destroy(): void
  {
    if ( !this.alive )
      return;

    this.stopTrail();
    this.alive = false;
    this.removeGroundShadow();

    SceneManager.world.remove( this.mesh );

    PhysicsWorld.getWorld().removeRigidBody(
      this.body
    );
  }
}
